import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';
import AppImage from '@/components/ui/AppImage';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  name: string;
  icon: string;
  href: string;
}

interface FooterProps {
  sections: FooterSection[];
  socialLinks: SocialLink[];
  contactInfo: {
    email: string;
    phone: string;
    secondaryPhone?: string;
    address: string;
  };
}

const Footer = ({ sections, socialLinks, contactInfo }: FooterProps) => {
  const { t } = useLanguage();

  return (
    <footer className="bg-gradient-luxury text-primary-foreground py-16 lg:py-20 border-t border-accent/10">
      <div className="mx-auto max-w-content px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            {/* Logo */}
            <Link 
              href="/home" 
              className="flex items-center gap-3 mb-8 transition-luxury hover:opacity-80 group"
              aria-label="Gold Dreams Home"
            >
              <AppImage
                src="/assets/images/output-onlinepngtools-1769787392075.png"
                alt="Gold Dreams Logo"
                width={48}
                height={48}
                className="transition-luxury group-hover:scale-105 invert brightness-0"
              />
              <span className="font-heading text-2xl font-bold tracking-tight">
                GOLD DREAMS
              </span>
            </Link>

            <p className="text-base text-primary-foreground/80 leading-relaxed mb-8">
              {t('footer.company')}
            </p>
          </div>

          {/* Footer Sections */}
          {sections.map((section, index) => (
            <div key={index}>
              <h3 className="font-heading text-lg font-semibold mb-6 tracking-tight">
                {section.title}
              </h3>
              <ul className="space-y-4">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="font-body text-base text-primary-foreground/80 hover:text-accent transition-luxury inline-flex items-center gap-2 group"
                    >
                      <Icon 
                        name="ChevronRightIcon" 
                        size={16} 
                        className="opacity-0 group-hover:opacity-100 transition-luxury -ml-6 group-hover:ml-0"
                        variant="outline"
                      />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Section */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-6 tracking-tight">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${contactInfo.phone}`} 
                  className="font-body text-base text-primary-foreground/80 hover:text-accent transition-luxury inline-flex items-center gap-2"
                >
                  <Icon name="PhoneIcon" size={16} variant="outline" className="text-accent" />
                  {contactInfo.phone}
                </a>
              </li>
              {contactInfo.secondaryPhone && (
                <li>
                  <a 
                    href={`tel:${contactInfo.secondaryPhone}`} 
                    className="font-body text-base text-primary-foreground/80 hover:text-accent transition-luxury inline-flex items-center gap-2"
                  >
                    <Icon name="PhoneIcon" size={16} variant="outline" className="text-accent" />
                    {contactInfo.secondaryPhone}
                  </a>
                </li>
              )}
              <li>
                <a 
                  href={`mailto:${contactInfo.email}`} 
                  className="font-body text-base text-primary-foreground/80 hover:text-accent transition-luxury inline-flex items-center gap-2"
                >
                  <Icon name="EnvelopeIcon" size={16} variant="outline" className="text-accent" />
                  {contactInfo.email}
                </a>
              </li>
              <li className="pt-2">
                <div className="font-body text-base text-primary-foreground/80 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <Icon name="MapPinIcon" size={16} variant="outline" className="text-accent mt-1 flex-shrink-0" />
                    <span>{contactInfo.address}</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/10 pt-10 flex flex-col md:flex-row justify-between items-center gap-6 text-base text-primary-foreground/70">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <p>&copy; {new Date().getFullYear()} GOLD DREAMS. {t('footer.copyright')}</p>
          </div>
            
          {/* Legal Links - Right Aligned */}
          <div className="flex gap-4 md:gap-6 text-primary-foreground/70">
            <Link href="/imprint" className="hover:text-accent transition-luxury">
              {t('footer.imprint')}
            </Link>
            <Link href="/privacy-policy" className="hover:text-accent transition-luxury">
              {t('footer.privacy')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;